import React from "react";
import { View } from "react-native";
import {
  Text,
  theme,
  formatCurrency,
  useResponsive,
} from "@clearabee/ui-library-native";
import { debounce, FlatCatalogue } from "@clearabee/ui-sdk";
import { Input } from "components/Core/Input/Input";
import { ids, styles } from "./SurchargeSelection.styles";
import placeholderImage from "lib/images/placeholder.png";
import { ProhibitedItem } from "lib/helpers/tooltipHelper";
import { Image } from "components/Image/Image";
import { useFormContext } from "lib/hooks/useFormContext";

export interface SurchargeSelectionProps {
  surcharges: FlatCatalogue["items"];
  extrasTooltip: string[];
  prohibitedItemsTooltip: ProhibitedItem[];
  onSurchargeSelection: (sku: string, value: number) => void;
}

export const SurchargeSelection = ({
  surcharges,
  extrasTooltip,
  prohibitedItemsTooltip,
  onSurchargeSelection,
}: SurchargeSelectionProps): React.ReactElement => {
  const { screens } = useResponsive();
  const { Field } = useFormContext();

  return (
    <View style={styles.surchargePanel} dataSet={{ media: ids.surchargePanel }}>
      {surcharges.map(({ title, price, sku, img }, index) => (
        <View
          key={sku}
          style={[
            styles.surchargeItem,
            index === 0 && styles.surchargeFirstItem,
            index === surcharges.length - 1 && styles.surchargeLastItem,
            extrasTooltip.includes(sku) &&
              screens.large && { backgroundColor: theme.colors.accent.base },
          ]}
        >
          {!extrasTooltip.length &&
            !!prohibitedItemsTooltip.length &&
            screens.large && <View style={styles.overlay} />}
          <View
            style={[
              styles.surchargeItemContent,
              index === surcharges.length - 1 && {
                borderBottomColor: "transparent",
              },
            ]}
          >
            {!(screens.large && !screens.xlarge) && (
              <Image
                source={img ?? placeholderImage}
                resizeMode="contain"
                style={styles.surchargeImage}
              />
            )}
            <View>
              <Text fontSize="xsmall">{title}</Text>
              <Text fontSize="small" styles={{ fontFamily: "ProximaSemiBold" }}>
                {price ? formatCurrency(price) : "Free"}
              </Text>
            </View>
            <View
              style={{
                marginLeft: "auto",
              }}
            >
              <Field name={`extras.${sku}`} styles={{ marginLeft: "auto" }}>
                {({ field }) => (
                  <Input.Quantity
                    {...field}
                    onChange={(value) => {
                      debounce(() => onSurchargeSelection(sku, value), 300);
                      field.onChange(value);
                    }}
                    min={0}
                    max={99}
                    variant="small"
                  />
                )}
              </Field>
            </View>
          </View>
        </View>
      ))}
    </View>
  );
};
